/*
 * Description:
 * ---------------------
 * Every sentence the Software Updates screen shows (UpdatesPanel), as pure
 * functions of what notabox-server answers at /api/v1/updates
 * (server/pkg/releases). A site's sentence comes from what its box last said
 * of itself; the words never decide anything.
 *
 * ! file components/updateWords.js
 */

export const NOT_ON_SERVER = "This Weavian server does not offer software updates yet.";
export const NO_SITES = "No sites to show.";
export const UNASSIGNED = "Not assigned to a customer";

/* A release as people are shown it: 202610142215 is 2026.10.14.2215. */
export const version = (serial) => {
  const s = String(serial || "");
  return s.length === 12 ? `${s.slice(0, 4)}.${s.slice(4, 6)}.${s.slice(6, 8)}.${s.slice(8)}` : s;
};

export const day = (iso) => new Date(iso).toLocaleDateString([], { day: "numeric", month: "short" });

/* A time of day at the site, in its own time zone when the box names one. */
export const atSite = (iso, zone) => {
  const opts = { hour: "2-digit", minute: "2-digit", hourCycle: "h23" };
  try {
    return new Date(iso).toLocaleTimeString([], { ...opts, timeZone: zone });
  } catch {
    return new Date(iso).toLocaleTimeString([], opts);
  }
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const endHour = (p) => `${String((Number(p.start.slice(0, 2)) + p.hours) % 24).padStart(2, "0")}${p.start.slice(2)}`;

/* How a customer's boxes take releases. */
export const policy = (p) => (p.auto
  ? `Each site installs new releases by itself between ${p.start} and ${endHour(p)}, in its own time zone, checks the new version works, and goes back to the one before by itself if it does not.`
  : "Sites install new releases only when someone updates them from here.");

export const AUTO = "Install updates by themselves";

const lastTry = (l) => {
  const why = l.why ? `: ${l.why}` : "";
  switch (l.result) {
    case "held": return `It waited, as the site was busy${why}. It tries again in its update hour.`;
    case "aborted": return `Its last try changed nothing${why}.`;
    case "rolled_back": return `It went back to its version before by itself${why}. Weavian has paused that release for every site.`;
    case "failed": return `Its last update failed${why}. Weavian has paused that release for every site.`;
    case "refused": return `It refused release ${version(l.serial)}${why}.`;
    default: return "";
  }
};

/*
 * What a site runs, in a few words.
 */
export const runs = (site) => {
  const r = site.report;
  if (!r) return "Older software";
  if (!r.serial) return `A build made by hand${r.build ? ` (${r.build.slice(0, 12)})` : ""}`;
  return version(r.serial);
};

/*
 * What happens next at a site, and what its last try did: { text, tone },
 * tone "bad" for a site that needs someone, "wait" for one in the middle of
 * something or with a release to install, "ok" for one up to date, ""
 * otherwise. where is where the person reading updates it, as the page reads
 * ("from here"), empty when they may not.
 */
export const next = (site, where) => {
  const r = site.report;
  if (!r) return { text: `Its software is from before updates from the console. Weavian updates it once by hand${where ? `, and it can be updated ${where} from then on` : ""}.`, tone: "wait" };
  const last = r.last && r.last.result !== "updated" && (!r.serial || r.last.serial > r.serial) ? lastTry(r.last) : "";
  const bad = r.last && ["rolled_back", "failed"].includes(r.last.result) && r.last.serial > (r.serial || 0);
  const join = (text, tone) => ({ text: [text, last].filter(Boolean).join(" "), tone: bad ? "bad" : tone });
  if (r.cannot) return join(`It cannot update itself: ${r.cannot}.`, "wait");
  if (site.ask?.serial) return join(`Asked to update to ${version(site.ask.serial)} now${site.ask.by ? ` by ${site.ask.by}` : ""}. It starts within a minute.`, "wait");
  switch (r.state) {
    case "installing": return join("Updating now. It is back within a minute, or goes back to its version before by itself.", "wait");
    case "fetching": return join(`Downloading ${version(r.target)}.`, "wait");
    case "waiting": return join(r.at ? `Updates to ${version(r.target)} at ${atSite(r.at, r.zone)}, its own time.` : `Release ${version(r.target)} is ready for it${where ? `: update it ${where}` : ""}.`, "wait");
    default:
  }
  if (!r.serial) return join(site.available && where ? `Not on a release. Updating it ${where} moves it onto releases.` : "Not on a release.", "");
  if (site.available > r.serial) return join(`Release ${version(site.available)} is available${where ? `: update it ${where}` : ""}.`, "wait");
  return join("Up to date.", "ok");
};

export const restartNeeded = "System updates are installed and wait for a restart.";

/* The screen's first sentence, for every site the person sees. */
export const lead = (sites) => {
  const bad = sites.filter((s) => next(s).tone === "bad");
  if (bad.length) return `${plural(bad.length, "site", "sites")} went back to the version before after an update. That release is paused while Weavian looks at it.`;
  const busy = sites.filter((s) => ["installing", "fetching"].includes(s.report?.state));
  if (busy.length) return `${plural(busy.length, "site is", "sites are")} updating now.`;
  const waiting = sites.filter((s) => s.report?.state === "waiting");
  if (waiting.length) return `${plural(waiting.length, "site", "sites")} will update in ${waiting.length === 1 ? "its" : "their"} update hour.`;
  const behind = sites.filter((s) => s.report && s.available > (s.report.serial || 0));
  if (behind.length) return `${plural(behind.length, "site has a release", "sites have a release")} to install.`;
  const old = sites.filter((s) => !s.report);
  if (old.length) return `${plural(old.length, "site needs", "sites need")} one update by hand from Weavian before ${old.length === 1 ? "it" : "they"} can be updated from here.`;
  if (!sites.length) return NO_SITES;
  return sites.length === 1 ? "Your site is up to date." : `All ${sites.length} sites are up to date.`;
};

/* Where a release stands, in a sentence. */
export const releaseState = (rel, now = Date.now()) => {
  if (rel.paused) return { text: `Paused: ${rel.paused}${rel.paused_by ? ` (${rel.paused_by})` : ""}.`, tone: "bad" };
  const done = rel.updated ? ` ${plural(rel.updated, "box has", "boxes have")} updated to it.` : "";
  if (now < new Date(rel.out_at).getTime()) return { text: `Running on Weavian's own boxes first. Customers' sites get it from ${day(rel.out_at)}.${done}`, tone: "wait" };
  if (!rel.proven) return { text: `Waiting for one of Weavian's own boxes to run it before customers' sites get it.${done}`, tone: "wait" };
  return { text: `Out to customers' sites since ${day(rel.out_at)}.${done}`, tone: "" };
};

export const ask = {
  now: (node, rel) => ({
    title: `Update ${node} now?`,
    message: `It installs the newest release${rel ? ` (${version(rel)})` : ""} now and is offline for a few seconds while it restarts. If the new version does not work, it goes back to the one before by itself.`,
    confirmLabel: "Update now",
  }),
  force: (node) => ({
    title: `Update ${node} even though it is busy?`,
    message: "It updates while the site is in use: a call in progress drops, and every device there is offline for a few seconds.",
    confirmLabel: "Update anyway",
  }),
  restart: (node) => ({
    title: `Restart ${node} now?`,
    message: "It restarts to finish the system updates it installed, and the site is offline for about a minute.",
    confirmLabel: "Restart",
  }),
  off: (name) => ({
    title: `Stop automatic updates for ${name}?`,
    message: "Its sites then install new releases, security fixes included, only when someone updates them from here.",
    confirmLabel: "Stop automatic updates",
  }),
  pause: (rel) => ({
    title: `Pause release ${version(rel)}?`,
    message: "No site that does not run it yet gets it, until it is resumed. Sites that run it keep it.",
    confirmLabel: "Pause",
  }),
};

export const cols = { site: "Site", runs: "Runs", next: "What happens next" };
export const RELEASES = "Releases";
export const RELEASES_MEANS = "Each release runs on Weavian's own boxes first. A customer's sites get it after that, one site first and the rest a day later. A site that goes back to its version before pauses the release for every site.";
